import React from 'react';
import SEO from '../components/common/SEO';
import './TermsOfServicePage.css';

const TermsOfServicePage: React.FC = () => {
  return (
    <>
      <SEO 
        title="Terms of Service - SolBlox | Flip. Spin. Win."
        description="Terms of Service for SolBlox - Solana-based gaming platform. Read our terms and conditions for using our coinflip and jackpot games."
        url="https://solblox.com/terms"
      />
      <div className="terms-page">
        <div className="terms-container">
          <div className="terms-header">
            <h1>Terms of Service</h1>
            <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          <div className="terms-content">
            <section className="terms-section">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing and using SolBlox ("the Platform", "we", "us", or "our"), you accept and agree to be bound by the terms and provision of this agreement. 
                If you do not agree to abide by the above, please do not use this service.
              </p>
              <div className="beta-notice" style={{background: '#404040', padding: '15px', borderRadius: '8px', margin: '15px 0', color: 'white'}}>
                <strong>⚠️ BETA NOTICE:</strong> SolBlox is currently in beta testing. You may encounter bugs, errors, or temporary service interruptions. 
                For support, contact our Discord staff or reach out to us on Twitter.
              </div>
            </section>

            <section className="terms-section">
              <h2>2. Description of Service</h2>
              <p>
                SolBlox is a Solana blockchain-based coinflip gaming platform that offers provably fair games including:
              </p>
              <ul>
                <li><strong>Coinflip Games:</strong> Head-to-head coin flipping games where players can bet SOL on the outcome</li>
                <li><strong>Bot Games:</strong> Games against our automated house system when no other players are available</li>
              </ul>
              <p>
                All games use provably fair algorithms with server seeds, client seeds, and nonces to ensure transparent and verifiable randomness.
              </p>
            </section>

            <section className="terms-section">
              <h2>3. Eligibility and Account Requirements</h2>
              <div className="age-requirement" style={{background: '#0f0000', padding: '15px', borderRadius: '8px', margin: '15px 0', color: 'white'}}>
                <h3 style={{margin: '0 0 10px 0', color: 'white'}}>🔞 AGE REQUIREMENT - 18+ ONLY</h3>
                <p style={{margin: 0}}><strong>You must be at least 18 years old to use SolBlox.</strong> By using our service, you represent and warrant that you are of legal gambling age in your jurisdiction and that you have the legal capacity to enter into this agreement.</p>
              </div>
              
              <h3>3.2 Account Registration</h3>
              <p>To participate in games, you must:</p>
              <ul>
                <li>Create an account with a valid email address and unique username</li>
                <li>Connect a Solana wallet (Phantom, Solflare, or other supported wallets)</li>
                <li>Maintain the security of your account credentials</li>
                <li>Provide accurate information during registration</li>
              </ul>

              <h3>3.3 Prohibited Jurisdictions</h3>
              <p>
                SolBlox is not available to residents of jurisdictions where online gambling is prohibited by law. 
                You are responsible for ensuring that your use of our platform complies with local laws.
              </p>
            </section>

            <section className="terms-section">
              <h2>4. Deposits, Withdrawals, and SOL Transactions</h2>
              <h3>4.1 Deposits</h3>
              <p>
                Deposits are made by sending SOL to your unique deposit address generated upon account creation. 
                Our system monitors the Solana blockchain for incoming transactions and credits your account balance automatically.
              </p>

              <h3>4.2 Minimum Amounts</h3>
              <ul>
                <li>Minimum deposit: 0.001 SOL</li>
                <li>Minimum bet: 0.001 SOL</li>
                <li>Minimum withdrawal: 0.01 SOL</li>
              </ul>

              <h3>4.3 Withdrawals</h3>
              <p>
                Withdrawal requests are processed manually by our team for security purposes. 
                Processing times may vary but typically occur within 24-48 hours. We reserve the right to request additional verification for large withdrawals.
              </p>

              <h3>4.4 Transaction Fees</h3>
              <p>
                Solana network fees apply to all blockchain transactions. SolBlox does not charge additional fees for deposits or withdrawals, 
                but standard Solana network fees will be deducted from withdrawal amounts.
              </p>
            </section>

            <section className="terms-section">
              <h2>5. Game Rules and Mechanics</h2>
              <h3>5.1 Coinflip Games</h3>
              <ul>
                <li>Players create games by choosing heads or tails and setting a bet amount</li>
                <li>Other players can join by accepting the opposite side</li>
                <li>If no player joins, you may call our bot to complete the game</li>
                <li>Winners receive 1.95x their bet amount (5% house edge)</li>
              </ul>



              <h3>5.3 Provably Fair System</h3>
              <p>
                All games use cryptographically secure random number generation with:
              </p>
              <ul>
                <li>Server seeds (generated and hashed before games)</li>
                <li>Client seeds (provided by players)</li>
                <li>Nonces (incremental counters)</li>
              </ul>
              <p>Game outcomes can be verified on our Provably Fair page using the provided seeds and algorithms.</p>
            </section>

            <section className="terms-section">
              <h2>6. House Edge and Payouts</h2>
              <p>
                SolBlox operates with a transparent house edge system:
              </p>
              <ul>
                <li>Coinflip games: 5% house edge (1.95x payout on wins)</li>
                <li>Bot games: Same house edge as player-vs-player games</li>
              </ul>
              <p>
                House edge funds platform operations, security measures, and ensures sufficient liquidity for bot games.
              </p>
            </section>

            <section className="terms-section">
              <h2>7. Affiliate Program</h2>
              <p>
                SolBlox offers an affiliate program where users can earn commissions by referring new players:
              </p>
              <ul>
                <li>Generate unique affiliate codes through your account settings</li>
                <li>Earn percentage of referred users' wagering activity</li>
                <li>Affiliate earnings are credited to your account balance</li>
                <li>Abuse of the affiliate system may result in account suspension</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>8. Chat System and Community Guidelines</h2>
              <h3>8.1 Chat Rules</h3>
              <p>Our platform includes a real-time chat system. Users must:</p>
              <ul>
                <li>Maintain respectful communication</li>
                <li>Avoid spam, harassment, or offensive language</li>
                <li>Not share external links or promotional content</li>
                <li>Not attempt to manipulate or deceive other users</li>
              </ul>

              <h3>8.2 Moderation</h3>
              <p>
                We reserve the right to mute, ban, or restrict users who violate chat guidelines. 
                Repeated violations may result in permanent account suspension.
              </p>
            </section>

            <section className="terms-section">
              <h2>9. Account Security and Responsibilities</h2>
              <h3>9.1 User Responsibilities</h3>
              <ul>
                <li>Maintain the security of your account credentials</li>
                <li>Immediately report any unauthorized account access</li>
                <li>Keep your connected wallet secure</li>
                <li>Verify all transaction details before confirming</li>
              </ul>

              <h3>9.2 Platform Security</h3>
              <p>
                SolBlox implements industry-standard security measures including:
              </p>
              <ul>
                <li>Encrypted data transmission and storage</li>
                <li>Rate limiting to prevent abuse</li>
                <li>Comprehensive audit logging</li>
                <li>Regular security assessments</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>10. Prohibited Activities</h2>
              <p>Users are strictly prohibited from:</p>
              <ul>
                <li>Using multiple accounts to gain unfair advantages</li>
                <li>Attempting to manipulate game outcomes</li>
                <li>Exploiting bugs or vulnerabilities</li>
                <li>Using automated tools or bots (except our official bot)</li>
                <li>Money laundering or other illegal financial activities</li>
                <li>Sharing accounts or credentials with others</li>
                <li>Circumventing security measures or restrictions</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>11. Suspension and Termination</h2>
              <h3>11.1 Account Suspension</h3>
              <p>
                We may suspend accounts for violations of these terms, suspicious activity, or security concerns. 
                Suspended users will be notified of the reason and duration when possible.
              </p>

              <h3>11.2 Account Termination</h3>
              <p>
                Severe or repeated violations may result in permanent account termination. 
                In such cases, remaining balances may be forfeited depending on the nature of the violation.
              </p>

              <h3>11.3 Voluntary Account Closure</h3>
              <p>
                Users may request account closure at any time. Remaining balances will be processed for withdrawal 
                according to our standard procedures.
              </p>
            </section>

            <section className="terms-section">
              <h2>12. Disclaimers and Limitations</h2>
              <h3>12.1 Service Availability</h3>
              <p>
                SolBlox strives for 99.9% uptime but cannot guarantee uninterrupted service. 
                We may perform maintenance, updates, or emergency repairs that temporarily affect availability.
              </p>

              <h3>12.2 Blockchain Dependencies</h3>
              <p>
                Our service depends on the Solana blockchain network. We are not responsible for:
              </p>
              <ul>
                <li>Solana network outages or congestion</li>
                <li>Failed transactions due to network issues</li>
                <li>Changes to Solana protocol or fees</li>
              </ul>

              <h3>12.3 Limitation of Liability</h3>
              <p>
                SolBlox's liability is limited to the amount in your account balance. 
                We are not liable for indirect, consequential, or punitive damages.
              </p>
            </section>

            <section className="terms-section">
              <h2>13. Privacy and Data Protection</h2>
              <p>
                We collect and process personal data in accordance with our Privacy Policy. 
                This includes account information, transaction history, and usage analytics to improve our service.
              </p>
              <ul>
                <li>We do not sell personal data to third parties</li>
                <li>Data is encrypted and stored securely</li>
                <li>Users may request data deletion upon account closure</li>
                <li>We comply with applicable data protection regulations</li>
              </ul>
            </section>

            <section className="terms-section">
              <h2>14. Intellectual Property</h2>
              <p>
                All content, trademarks, and intellectual property on SolBlox are owned by us or our licensors. 
                Users may not reproduce, distribute, or create derivative works without explicit permission.
              </p>
            </section>

            <section className="terms-section">
              <h2>15. Dispute Resolution</h2>
              <h3>15.1 Customer Support</h3>
              <p>
                For disputes or issues, users should first contact our support team through the platform's 
                official channels. We aim to resolve issues promptly and fairly.
              </p>

              <h3>15.2 Arbitration</h3>
              <p>
                Disputes that cannot be resolved through customer support may be subject to binding arbitration 
                in accordance with the laws of our operating jurisdiction.
              </p>
            </section>

            <section className="terms-section">
              <h2>16. Changes to Terms</h2>
              <p>
                SolBlox reserves the right to modify these Terms of Service at any time. 
                Users will be notified of significant changes through the platform or email. 
                Continued use after changes constitutes acceptance of the new terms.
              </p>
            </section>

            <section className="terms-section">
              <h2>17. Governing Law</h2>
              <p>
                These Terms of Service are governed by and construed in accordance with applicable laws. 
                Any legal proceedings shall be conducted in the appropriate jurisdiction.
              </p>
            </section>

            <section className="terms-section">
              <h2>18. Contact Information and Support</h2>
              <p>
                For questions about these Terms of Service, technical support, or reporting issues during our beta phase, please contact us through:
              </p>
              <ul>
                <li><strong>Discord:</strong> Join our Discord server and contact our staff members for immediate assistance</li>
                <li><strong>Twitter:</strong> Reach out to us on Twitter for support and updates</li>
                <li>Platform support system (when logged in)</li>
              </ul>
              <p>
                <strong>Beta Support:</strong> As we are in beta testing, our Discord and Twitter channels are the fastest ways to get help with any bugs, errors, or issues you encounter.
              </p>
            </section>

            <section className="terms-section">
              <h2>19. Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions 
                will continue to be valid and enforceable to the fullest extent permitted by law.
              </p>
            </section>

            <section className="terms-section acknowledgment">
              <h2>Acknowledgment</h2>
              <p>
                By using SolBlox, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. 
                You also acknowledge the risks associated with cryptocurrency gambling and confirm that you are participating voluntarily.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfServicePage;